<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();

// Form data
const formData = ref({
  name: '',
  description: '',
  color: '#1f2937',
});

// Default colors using design system
const defaultColors = [
  '#1f2937', // n-slate-12 (dark)
  '#dc2626', // red-600
  '#ea580c', // orange-600
  '#ca8a04', // yellow-600
  '#16a34a', // green-600
  '#0891b2', // cyan-600
  '#2563eb', // blue-600
  '#7c3aed', // violet-600
  '#c2410c', // orange-700
  '#be123c', // rose-700
];

// Validation
const errors = ref({});

const canSave = computed(() => {
  return formData.value.name.trim().length > 0;
});

const resetForm = () => {
  formData.value = {
    name: '',
    description: '',
    color: '#1f2937',
  };
  errors.value = {};
};

const handleClose = () => {
  resetForm();
  emit('close');
};

const handleSave = () => {
  if (!canSave.value) {
    errors.value.name = t('KANBAN.PIPELINE_MODAL.VALIDATION.NAME_REQUIRED');
    return;
  }

  emit('save', {
    name: formData.value.name.trim(),
    description: formData.value.description.trim(),
    color: formData.value.color,
  });
};

// Reset form when modal is closed
watch(() => props.show, (newValue) => {
  if (!newValue) {
    resetForm();
  }
});
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="handleClose"
  >
    <div class="bg-n-background border border-n-weak rounded-xl shadow-xl max-w-md w-full mx-4">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('KANBAN.ADD_COLUMN') }}
        </h2>
        <button
          @click="handleClose"
          class="text-n-slate-9 hover:text-n-slate-12 transition-colors"
        >
          <i class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 space-y-4">
        <div>
          <Input
            v-model="formData.name"
            :label="t('KANBAN.PIPELINE_MODAL.STAGES.NAME_LABEL')"
            :placeholder="t('KANBAN.PIPELINE_MODAL.STAGES.NAME_PLACEHOLDER')"
            :error="errors.name"
            required
          />
        </div>

        <div>
          <TextArea
            v-model="formData.description"
            :label="t('KANBAN.PIPELINE_MODAL.STAGES.DESCRIPTION_LABEL')"
            :placeholder="t('KANBAN.PIPELINE_MODAL.STAGES.DESCRIPTION_PLACEHOLDER')"
            rows="3"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('KANBAN.PIPELINE_MODAL.STAGES.COLOR_LABEL') }}
          </label>
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="color in defaultColors"
              :key="color"
              @click="formData.color = color"
              class="w-8 h-8 rounded-full border-2 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              :class="{
                'border-n-slate-12 scale-110 ring-2 ring-blue-500 ring-offset-2': formData.color === color,
                'border-n-weak hover:border-n-slate-9': formData.color !== color,
              }"
              :style="{ backgroundColor: color }"
              :title="color"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 p-6 border-t border-n-weak bg-n-solid-1">
        <NextButton
          slate
          @click="handleClose"
          :disabled="loading"
        >
          {{ t('KANBAN.PIPELINE_MODAL.STAGES.CANCEL') }}
        </NextButton>
        <NextButton
          @click="handleSave"
          :disabled="!canSave || loading"
          :loading="loading"
        >
          {{ t('KANBAN.PIPELINE_MODAL.STAGES.ADD_STAGE') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
